import * as React from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'
import { F0Alert } from '@factorialco/f0-react'
import { AtsCandidateSourceCategoriesEnum } from 'generated/resources/datatable.graphql'

import Modal from 'modules/core/components/Modal'
import Select from 'modules/core/components/SelectField'
import TextField from 'modules/core/components/TextField'
import { useForm } from 'modules/core/hooks/useForm'
import type { FormErrors } from 'modules/core/hooks/useForm'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { CandidateSource } from 'modules/ats/hooks/candidateSources/useCandidateSources'
import { useUpdateCandidateSource } from 'modules/ats/hooks/candidateSources/useUpdateCandidateSource'
import { candidateSourceCategoryOptions } from 'modules/ats/lib/utils/candidateSources'

interface EditCandidateSourceFormValues {
  id: string
  category: AtsCandidateSourceCategoriesEnum
  name: string
}

interface Props {
  source: CandidateSource
  onClose: () => void
}

export const EditCandidateSourceModal = ({ onClose, source }: Props) => {
  const { update } = useUpdateCandidateSource(source)

  const form = useForm<EditCandidateSourceFormValues>({
    defaultValues: {
      id: source.id,
      name: source.name,
      category: source.category as AtsCandidateSourceCategoriesEnum,
    },
    validate: (data) => {
      const errors: FormErrors<EditCandidateSourceFormValues> = {}

      if (!data.category) {
        errors.category = i18n.t('common.empty')
      }

      if (!data.name) {
        errors.name = i18n.t('common.empty')
      }

      return errors
    },
    onSubmit: async (data, { setErrors }) => {
      const result = await update(data)

      const handled = handleResult(result, (errors) => {
        setErrors(errors)
      })

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
        onClose()
      }

      return handled
    },
  })

  return (
    <Modal
      title={i18n.t('ats.manage_sources_customization.edit_modal.title')}
      description={i18n.t('ats.manage_sources_customization.edit_modal.description')}
      illustration="document-edit"
      onClose={onClose}
      onSubmit={() => form.handleSubmit()}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button disabled={form.isSubmitting} submit>
            {i18n.t('ats.manage_sources_customization.edit_modal.save_button')}
          </Button>
        </>
      }
    >
      <Box gap="s24">
        <Select
          none={i18n.t('common.select')}
          label={i18n.t('ats.manage_sources_customization.edit_modal.category_label')}
          options={candidateSourceCategoryOptions()}
          value={form.values.category}
          error={form.touched.category ? form.errors.category : undefined}
          onChange={(value) =>
            form.setValues({
              ...form.values,
              category: value as AtsCandidateSourceCategoriesEnum,
            })
          }
          mandatory
        />
        <TextField
          label={i18n.t('ats.manage_sources_customization.edit_modal.name_label')}
          value={form.values.name}
          onChange={(value) => form.setValues({ ...form.values, name: value })}
          error={form.touched.name ? form.errors.name : undefined}
          mandatory
        />
        <F0Alert
          variant="warning"
          title={i18n.t('ats.manage_sources_customization.edit_modal.message')}
          description=""
        />
      </Box>
    </Modal>
  )
}
